/**
 * Prozedurales Animations-Framework (BATTLE-14) — rein render-seitig,
 * getriggert aus dem EventLog (Architektur-Regel 5 bleibt intakt).
 * Stil (docs/11): kurz & snappy — Anticipation, Overshoot, Squash & Stretch, Recoil.
 */
import type { Pose } from './species/types'

const ATTACK_MS = 260
const HIT_MS = 180
const CAST_MS = 450
const DEATH_MS = 500

interface UnitAnim {
  attackStart: number
  attackDirX: number
  attackDirY: number
  hitStart: number
  castStart: number
  deathStart: number
}

function blank(): UnitAnim {
  return { attackStart: -1e9, attackDirX: 1, attackDirY: 0, hitStart: -1e9, castStart: -1e9, deathStart: -1e9 }
}

/** easeOutBack — Overshoot fuer snappy Rueckkehr */
function easeOutBack(t: number): number {
  const c = 1.70158
  const u = t - 1
  return 1 + (c + 1) * u * u * u + c * u * u
}

export class AnimDirector {
  private anims = new Map<number, UnitAnim>()

  reset(): void {
    this.anims.clear()
  }

  private get(id: number): UnitAnim {
    let a = this.anims.get(id)
    if (!a) {
      a = blank()
      this.anims.set(id, a)
    }
    return a
  }

  /** Angriff Richtung Ziel (dir normalisiert) */
  triggerAttack(id: number, nowMs: number, dirX: number, dirY: number): void {
    const a = this.get(id)
    a.attackStart = nowMs
    const len = Math.hypot(dirX, dirY) || 1
    a.attackDirX = dirX / len
    a.attackDirY = dirY / len
  }

  triggerHit(id: number, nowMs: number): void {
    this.get(id).hitStart = nowMs
  }

  triggerCast(id: number, nowMs: number): void {
    this.get(id).castStart = nowMs
  }

  triggerDeath(id: number, nowMs: number): void {
    this.get(id).deathStart = nowMs
  }

  /**
   * Pose einer Unit zum Renderzeitpunkt. r = Unit-Radius (skaliert Lunge/Jiggle).
   * moving daempft das Idle-Wippen (Lauf-Bounce uebernimmt).
   */
  getPose(id: number, nowMs: number, r: number, moving: boolean): Pose {
    const a = this.anims.get(id) ?? blank()
    const pose: Pose = { squashX: 1, squashY: 1, rot: 0, dx: 0, dy: 0, flash: 0, attackSwing: 0, castPulse: 0 }

    // --- Idle: Atem-Wippen, Phase deterministisch aus Unit-Id ---
    const phase = (id * 0.83) % (Math.PI * 2)
    const idleT = nowMs / 1000
    const idle = Math.sin(idleT * 2.2 + phase)
    const idleAmp = moving ? 0.05 : 0.028
    pose.squashY += idle * idleAmp
    pose.squashX -= idle * idleAmp * 0.6
    if (moving) {
      // Lauf-Bounce: schnelleres Hopsen + leichte Neigung
      pose.dy -= Math.abs(Math.sin(idleT * 9 + phase)) * r * 0.09
      pose.rot += Math.sin(idleT * 9 + phase) * 0.04
    }

    // --- Attack: Anticipation (zurueck) -> Lunge (vor, Overshoot) -> Recoil ---
    const at = (nowMs - a.attackStart) / ATTACK_MS
    if (at >= 0 && at <= 1) {
      let lunge: number
      if (at < 0.28) {
        lunge = -0.35 * (at / 0.28) // Anticipation: zurueckziehen
      } else {
        lunge = easeOutBack(Math.min(1, (at - 0.28) / 0.5)) * (1 - Math.max(0, (at - 0.78) / 0.22))
      }
      const amp = r * 0.58 // F8: kraeftigere Attack-Lunge (mehr "Juice")
      pose.dx += a.attackDirX * lunge * amp
      pose.dy += a.attackDirY * lunge * amp
      pose.rot += a.attackDirX * lunge * 0.16
      // Squash beim Abstossen
      if (at < 0.28) {
        pose.squashY -= 0.08 * (at / 0.28)
        pose.squashX += 0.06 * (at / 0.28)
      }
      pose.attackSwing = Math.max(0, Math.min(1, (at - 0.2) / 0.55))
    }

    // --- Hit: Flash + Jiggle ---
    const ht = (nowMs - a.hitStart) / HIT_MS
    if (ht >= 0 && ht <= 1) {
      pose.flash = (1 - ht) * 0.85
      const jig = Math.sin(ht * Math.PI * 4) * (1 - ht)
      pose.dx += jig * r * 0.07
      pose.squashX += jig * 0.05
      pose.squashY -= jig * 0.05
    }

    // --- Cast: Puls (aufblaehen + zurueck) ---
    const ct = (nowMs - a.castStart) / CAST_MS
    if (ct >= 0 && ct <= 1) {
      const pulse = Math.sin(ct * Math.PI)
      pose.castPulse = pulse
      pose.squashX += pulse * 0.1
      pose.squashY += pulse * 0.1
      pose.dy -= pulse * r * 0.08
    }

    // --- Death: Squash flach ---
    const dt = (nowMs - a.deathStart) / DEATH_MS
    if (dt >= 0 && dt <= 1) {
      pose.squashY *= 1 - dt * 0.45
      pose.squashX *= 1 + dt * 0.25
      pose.dy += dt * r * 0.2
    }

    return pose
  }
}
